import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { StartField, StartFieldType } from "../../utils/workflow-run-utils"
import type { NodeProps } from "@xyflow/react"

import { ChevronDown, ChevronUp, Plus, Trash2, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

type FileMode = "none" | "optional" | "required"

const FILE_MODE_LABELS: Record<FileMode, string> = {
  none: "No files",
  optional: "Files optional",
  required: "Files required",
}

import { NodeEditDialog } from "./node-edit-dialog"
import { TrustedSourcesPicker } from "./trusted-sources-picker"
import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { deriveStartFieldKey } from "../../utils/workflow-run-utils"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"

interface FlowTrustedSources {
  sources: string[]
  mode: "only" | "preferred"
}

export function StartNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleRequiresInputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { requiresInput: checked })
    },
    [id, updateNodeData]
  )

  const handleFileModeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { fileMode: value as FileMode })
    },
    [id, updateNodeData]
  )

  // Multi-field input form (new). When present and non-empty, fields[] is the
  // authoritative source of run inputs; the legacy Text Input / Files controls
  // above remain for back-compat but are superseded.
  // Backfill missing keys (and normalize type) so the editor reads/commits the same
  // shape every consumer expects (resolveStartFields derives keys only when absent).
  // A field persisted without a key would otherwise render/commit inconsistently.
  const fields: StartField[] = (
    Array.isArray(data.fields) ? (data.fields as StartField[]) : []
  ).map((field, index) => {
    const type: StartFieldType = field.type === "file" ? "file" : "text"
    return {
      ...field,
      type,
      key: field.key && field.key.length > 0 ? field.key : deriveStartFieldKey(type, index),
    }
  })
  const hasFields = fields.length > 0

  const commitFields = useCallback(
    (next: StartField[]) => {
      updateNodeData(id, { fields: next })
    },
    [id, updateNodeData]
  )

  const handleAddField = useCallback(
    (type: StartFieldType) => {
      // Generate a key that doesn't collide with any existing one (removals can
      // otherwise re-introduce a previously used index).
      const existingKeys = new Set(fields.map((field) => field.key))
      let suffix = fields.length
      while (existingKeys.has(`${type}_${suffix}`)) suffix += 1
      const next: StartField[] = [
        ...fields,
        { type, label: "", required: false, key: `${type}_${suffix}` },
      ]
      commitFields(next)
    },
    [fields, commitFields]
  )

  const handleRemoveField = useCallback(
    (index: number) => {
      commitFields(fields.filter((_, i) => i !== index))
    },
    [fields, commitFields]
  )

  const handleFieldChange = useCallback(
    (index: number, patch: Partial<StartField>) => {
      commitFields(
        fields.map((field, i) => {
          if (i !== index) return field
          const next = { ...field, ...patch }
          // When the type changes, the `${type}_n` key must follow it. Otherwise a
          // field created as Text keeps its `text_*` key after becoming a File field
          // (and vice-versa), and the backend legacy-envelope fallback — which keys
          // files vs text off `key.startsWith("file")` — misclassifies it. Regenerate
          // a key that doesn't collide with the other fields.
          if (patch.type && patch.type !== field.type) {
            const otherKeys = new Set(fields.filter((_, j) => j !== index).map((f) => f.key))
            let suffix = index
            while (otherKeys.has(deriveStartFieldKey(patch.type, suffix))) suffix += 1
            next.key = deriveStartFieldKey(patch.type, suffix)
          }
          return next
        })
      )
    },
    [fields, commitFields]
  )

  const handleMoveField = useCallback(
    (index: number, direction: -1 | 1) => {
      const target = index + direction
      if (target < 0 || target >= fields.length) return
      const next = [...fields]
      const [moved] = next.splice(index, 1)
      next.splice(target, 0, moved)
      commitFields(next)
    },
    [fields, commitFields]
  )

  // Flow-level trusted sources (defaults applied across the whole flow)
  const flowTrustedSources: FlowTrustedSources =
    data.flowTrustedSources && typeof data.flowTrustedSources === "object"
      ? (data.flowTrustedSources as FlowTrustedSources)
      : { sources: [], mode: "only" }
  const flowTrustedSourceUrls: string[] = Array.isArray(flowTrustedSources.sources)
    ? flowTrustedSources.sources
    : []
  const flowTrustedMode: "only" | "preferred" =
    flowTrustedSources.mode === "preferred" ? "preferred" : "only"

  const toggleFlowTrustedSource = useCallback(
    (sourceUrl: string) => {
      const isActive = flowTrustedSourceUrls.includes(sourceUrl)
      const newSources = isActive
        ? flowTrustedSourceUrls.filter((url) => url !== sourceUrl)
        : [...flowTrustedSourceUrls, sourceUrl]
      updateNodeData(id, {
        flowTrustedSources: { sources: newSources, mode: flowTrustedMode },
      })
    },
    [id, flowTrustedSourceUrls, flowTrustedMode, updateNodeData]
  )

  const handleFlowTrustedModeChange = useCallback(
    (mode: "only" | "preferred") => {
      updateNodeData(id, {
        flowTrustedSources: { sources: flowTrustedSourceUrls, mode },
      })
    },
    [id, flowTrustedSourceUrls, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className={cn("space-y-3", isModal && "max-w-sm")}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs" htmlFor={`requires-input-${id}${isModal ? "-modal" : ""}`}>
          Text Input
        </Label>
        <Switch
          checked={(data.requiresInput as boolean) || false}
          className="nodrag scale-75"
          id={`requires-input-${id}${isModal ? "-modal" : ""}`}
          onCheckedChange={handleRequiresInputChange}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Files</Label>
        <Select
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          value={(data.fileMode as FileMode) || "none"}
          onValueChange={handleFileModeChange}
        >
          <SelectTrigger className="nodrag h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(FILE_MODE_LABELS).map(([value, label]) => (
              <SelectItem key={value} className="text-xs" value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2 border-t pt-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">Input fields</Label>
          <div className="flex items-center gap-1">
            <Button
              className="nodrag h-6 px-2 text-[10px]"
              size="sm"
              type="button"
              variant="outline"
              onClick={() => handleAddField("text")}
            >
              <Plus className="mr-0.5 size-3" />
              Text
            </Button>
            <Button
              className="nodrag h-6 px-2 text-[10px]"
              size="sm"
              type="button"
              variant="outline"
              onClick={() => handleAddField("file")}
            >
              <Plus className="mr-0.5 size-3" />
              File
            </Button>
          </div>
        </div>
        {hasFields ? (
          <>
            <p className="text-muted-foreground text-[10px]">
              When set, these replace the simple Text Input / Files toggles above at run time.
            </p>
            <div className="space-y-2">
              {fields.map((field, index) => (
                <div
                  key={`${field.key}-${index}`}
                  className="bg-muted/40 space-y-1.5 rounded-md border p-2"
                >
                  <div className="flex items-center gap-1">
                    <Select
                      value={field.type}
                      onValueChange={(value) =>
                        handleFieldChange(index, { type: value as StartFieldType })
                      }
                    >
                      <SelectTrigger className="nodrag h-7 w-[84px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem className="text-xs" value="text">
                          Text
                        </SelectItem>
                        <SelectItem className="text-xs" value="file">
                          File
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      className="nodrag h-7 flex-1 text-xs"
                      placeholder="Label"
                      value={field.label}
                      onChange={(event) => handleFieldChange(index, { label: event.target.value })}
                    />
                    <Button
                      aria-label="Move field up"
                      className="nodrag size-7 p-0"
                      disabled={index === 0}
                      size="icon"
                      type="button"
                      variant="ghost"
                      onClick={() => handleMoveField(index, -1)}
                    >
                      <ChevronUp className="size-3" />
                    </Button>
                    <Button
                      aria-label="Move field down"
                      className="nodrag size-7 p-0"
                      disabled={index === fields.length - 1}
                      size="icon"
                      type="button"
                      variant="ghost"
                      onClick={() => handleMoveField(index, 1)}
                    >
                      <ChevronDown className="size-3" />
                    </Button>
                    <Button
                      aria-label="Remove field"
                      className="nodrag text-destructive size-7 p-0"
                      size="icon"
                      type="button"
                      variant="ghost"
                      onClick={() => handleRemoveField(index)}
                    >
                      <Trash2 className="size-3" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-[10px]" htmlFor={`field-required-${id}-${index}`}>
                      Required
                    </Label>
                    <Switch
                      checked={field.required}
                      className="nodrag scale-75"
                      id={`field-required-${id}-${index}`}
                      onCheckedChange={(checked) => handleFieldChange(index, { required: checked })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-[10px]">
            Add custom text or file inputs collected when the flow runs.
          </p>
        )}
      </div>

      <div className="space-y-1 border-t pt-2">
        <div className="flex items-center gap-1">
          <Label className="text-xs">Flow trusted sources (defaults)</Label>
          <TrustedSourcesPicker
            selectedUrls={flowTrustedSourceUrls}
            trustedMode={flowTrustedMode}
            onModeChange={handleFlowTrustedModeChange}
            onToggleSource={toggleFlowTrustedSource}
          />
        </div>
        <p className="text-muted-foreground text-[10px]">
          Applied across the whole flow. Individual nodes can narrow these to their own selected
          sources.
        </p>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[150px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[4])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="text-center text-sm font-medium">
            Start
            <NodeEditDialog description="Edit start-node inputs." title="Start settings">
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3 p-3">
          {renderEditor()}

          <Handle position={Position.Right} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
